import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { PageHeader } from '../../../components/ui/PageHeader'
import { pb } from '../../../pocketbase'
import { COL_PRODUCT_CATEGORIES, COL_PRODUCT_COMPONENTS, COL_PRODUCTS } from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { useAuth } from '../../../context/AuthContext'
import { matchesListSearch } from '../../../lib/listSearch'
import { bomBreakdownFromComponents } from '../../../lib/productionCost'
import { unitLabel } from '../../../lib/units'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  alert,
  btn,
  cn,
  productionMainClass,
  productionWithCategoriesClass,
  searchInputClass,
  tableCellClass,
  tableClass,
  tableCompactCellClass,
  tableCompactHeadClass,
  tableHeadClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'
import type { ProductComponentRecord, ProductRecord } from '../../../types/records'
import { CategoryTreePanel, type ProductionCategoryFilter } from './CategoryTreePanel'

function ProductBomPanel({ productId }: { productId: string }) {
  const [rows, setRows] = useState<ProductComponentRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setErr('')
      setLoading(true)
      try {
        const list = await pb.collection(COL_PRODUCT_COMPONENTS).getFullList<ProductComponentRecord>({
          filter: `product="${productId}"`,
          expand: 'consumable.consumable_category',
          sort: '+sort',
        })
        if (!cancelled) setRows(list)
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(pocketbaseErrorMessage(e, 'Ошибка'))
          setRows([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productId])

  if (loading) {
    return <p className="m-0 text-sm text-su-text">Загрузка комплектации…</p>
  }
  if (err) {
    return <div className={alert('err')}>{err}</div>
  }
  if (rows.length === 0) {
    return <p className="m-0 text-sm text-su-text">Строк комплектации нет.</p>
  }

  const bd = bomBreakdownFromComponents(rows)

  return (
    <div className="rounded-su border border-su-border bg-su-surface-muted/50 p-3">
      <div className="overflow-x-auto">
        <table className={cn(tableClass, 'text-xs')}>
          <thead>
            <tr>
              <th className={tableCompactHeadClass}>#</th>
              <th className={tableCompactHeadClass}>Номенклатура</th>
              <th className={tableCompactHeadClass}>Количество</th>
              <th className={tableCompactHeadClass}>Ед. изм.</th>
              <th className={tableCompactHeadClass}>Сумма, ₽</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const c = r.expand?.consumable
              const qty = Number(r.quantity) || 0
              const price = c ? Number(c.price) || 0 : 0
              const line = Math.round(qty * price * 100) / 100
              return (
                <tr key={r.id} className={tableRowHoverClass}>
                  <td className={tableCompactCellClass}>{i + 1}</td>
                  <td className={tableCompactCellClass}>{c?.name ?? '—'}</td>
                  <td className={tableCompactCellClass}>{qty}</td>
                  <td className={tableCompactCellClass}>{c ? unitLabel(c.unit) : '—'}</td>
                  <td className={tableCompactCellClass}>
                    {line.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 border-t border-su-border pt-3 text-sm text-su-text-strong">
        {bd.hasLabor ? (
          <>
            <p className="mb-1 mt-0">
              <strong>Материалы:</strong> {bd.materials.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
            </p>
            <p className="mb-1 mt-0">
              <strong>Работа:</strong> {bd.labor.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
            </p>
            <p className="m-0">
              <strong>Итого (оценка):</strong> {bd.total.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
            </p>
          </>
        ) : (
          <p className="m-0">
            <strong>Себестоимость (оценка):</strong> {bd.total.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
          </p>
        )}
      </div>
    </div>
  )
}

export function ProductsListPage() {
  const { can } = useAuth()
  const [items, setItems] = useState<ProductRecord[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [catFilter, setCatFilter] = useState<ProductionCategoryFilter>({ kind: 'all' })
  const [searchQuery, setSearchQuery] = useState('')

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_PRODUCTS).getFullList<ProductRecord>({
        sort: 'name',
        expand: 'product_category',
      })
      setItems(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const byCategory = useMemo(() => {
    if (catFilter.kind === 'all') return items
    if (catFilter.kind === 'uncategorized') return items.filter((i) => !i.product_category)
    const set = new Set(catFilter.ids)
    return items.filter((i) => i.product_category && set.has(i.product_category))
  }, [items, catFilter])

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return byCategory
    return byCategory.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.name,
        row.code,
        row.expand?.product_category?.name,
        row.status,
        row.status === 'active' ? 'Действует' : row.status === 'draft' ? 'Черновик' : '',
        row.notes,
      ),
    )
  }, [byCategory, searchQuery])

  const newProductTo = useMemo(() => {
    if (catFilter.kind === 'subtree') {
      return `/production/products/new?category=${encodeURIComponent(catFilter.rootId)}`
    }
    return '/production/products/new'
  }, [catFilter])

  async function removeProduct(id: string) {
    if (!window.confirm('Удалить продукцию и всю комплектацию?')) return
    try {
      const comps = await pb.collection(COL_PRODUCT_COMPONENTS).getFullList({
        filter: `product="${id}"`,
      })
      await Promise.all(comps.map((c) => pb.collection(COL_PRODUCT_COMPONENTS).delete(c.id)))
      await pb.collection(COL_PRODUCTS).delete(id)
      if (expanded === id) setExpanded(null)
      await load()
    } catch (e: unknown) {
      window.alert(pocketbaseErrorMessage(e, 'Ошибка'))
    }
  }

  function statusRu(s: string) {
    if (s === 'active') return 'Действует'
    if (s === 'draft') return 'Черновик'
    return s || '—'
  }

  const canManageCategories =
    can(PERMISSION_KEYS.CONSUMABLES_WRITE) || can(PERMISSION_KEYS.PRODUCTS_WRITE)

  return (
    <div className={productionWithCategoriesClass}>
      <div className={productionMainClass}>
        <PageHeader
          asSection
          title="Продукция"
          lead="Новая позиция: наименование и комплектация из расходников. Нажмите на строку — раскроется состав и расчёт себестоимости."
        />
        {err && <div className={alert('err')}>{err}</div>}
        <div className={toolbarClass}>
          {can(PERMISSION_KEYS.PRODUCTS_WRITE) && (
            <Link to={newProductTo} className={btn('primary')}>
              Новая продукция
            </Link>
          )}
          <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
            Обновить
          </button>
          <div className="ml-auto min-w-0 w-full max-w-sm sm:w-auto">
            <input
              type="search"
              className={searchInputClass}
              placeholder="Поиск: название, код, категория, статус…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Поиск по продукции"
            />
          </div>
        </div>
        <div className={tableWrapClass}>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={cn(tableHeadClass, 'w-10')} />
                <th className={tableHeadClass}>Наименование</th>
                <th className={tableHeadClass}>Категория</th>
                <th className={tableHeadClass}>Код</th>
                <th className={tableHeadClass}>Статус</th>
                <th className={tableHeadClass} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>Загрузка…</em>
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>Нет продукции — создайте первую позицию.</em>
                  </td>
                </tr>
              )}
              {!loading &&
                items.length > 0 &&
                byCategory.length === 0 && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>В выбранной категории ничего нет.</em>
                  </td>
                </tr>
              )}
              {!loading &&
                byCategory.length > 0 &&
                displayedItems.length === 0 && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>По запросу ничего не найдено — измените текст поиска.</em>
                  </td>
                </tr>
              )}
              {!loading &&
                displayedItems.map((row) => (
                  <Fragment key={row.id}>
                    <tr
                      className={cn(
                        tableRowHoverClass,
                        expanded === row.id && 'bg-indigo-500/6',
                      )}
                    >
                      <td className={tableCellClass}>
                        <button
                          type="button"
                          className={btn('ghost', 'sm', 'min-w-8 px-2')}
                          aria-expanded={expanded === row.id}
                          title="Комплектация"
                          onClick={() => setExpanded((v) => (v === row.id ? null : row.id))}
                        >
                          {expanded === row.id ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className={tableCellClass}>
                        <button
                          type="button"
                          className="cursor-pointer border-none bg-transparent p-0 text-left font-inherit text-su-primary underline-offset-2 hover:underline"
                          onClick={() => setExpanded((v) => (v === row.id ? null : row.id))}
                        >
                          {row.name}
                        </button>
                      </td>
                      <td className={tableCellClass}>{row.expand?.product_category?.name ?? '—'}</td>
                      <td className={tableCellClass}>{row.code || '—'}</td>
                      <td className={tableCellClass}>{statusRu(row.status)}</td>
                      <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                        {can(PERMISSION_KEYS.PRODUCTS_WRITE) && (
                          <Link to={`/production/products/${row.id}`} className={btn('ghost', 'sm')}>
                            Изменить
                          </Link>
                        )}
                        {can(PERMISSION_KEYS.PRODUCTS_DELETE) && (
                          <button
                            type="button"
                            className={cn(btn('danger', 'sm'), 'ml-1')}
                            onClick={() => void removeProduct(row.id)}
                          >
                            Удалить
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === row.id && (
                      <tr className="bg-su-surface-muted/60">
                        <td className={cn(tableCellClass, 'p-3')} colSpan={6}>
                          <ProductBomPanel productId={row.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <CategoryTreePanel
        categoryCollection={COL_PRODUCT_CATEGORIES}
        title="Категории продукции"
        value={catFilter}
        onChange={setCatFilter}
        canManage={canManageCategories}
      />
    </div>
  )
}
